import { PROMOTION_TYPE, type RnbCalculatorPayload } from 'typings'
import { mapPromotionBase } from '..'
import {
  BuyAndWinBenefitVariant,
  BuyAndWinFreeShipping,
  BuyAndWinRestrictions,
  MaximumUnitPriceDiscount,
  ShippingApplyTo,
  type BuyAndWinEffect,
  type BuyAndWinPromotion,
} from '../../../../typings'
import { mapConditions } from '../conditions'
import { mapGift, mapGiftItems } from '../gift'
import {
  mapAccumulate,
  mapBudget,
  mapMaxUsagePerClient,
  mapSalesChannels,
  mapSellerIds,
  mapUsage,
} from '../restrictions'

export function mapTrigger(
  data: RnbCalculatorPayload
): BuyAndWinEffect['trigger'] {
  const {
    collections1BuyTogether,
    listSku1BuyTogether,
    minimumQuantityBuyTogether,
    enableBuyTogetherPerSku,
  } = data

  return collections1BuyTogether.length || listSku1BuyTogether.length
    ? {
        collections: collections1BuyTogether,
        skus: listSku1BuyTogether,
        minQuantity: minimumQuantityBuyTogether,
        sameItemsOnly: enableBuyTogetherPerSku,
      }
    : undefined
}

export function mapFreeShipping(
  data: RnbCalculatorPayload
): BuyAndWinFreeShipping {
  const { applyToAllShippings, isSlaSelected, slasIds } = data

  const methods = slasIds?.length
    ? slasIds.map((id) => ({ id, name: id }))
    : undefined
  if (methods) {
    return {
      type: BuyAndWinBenefitVariant.FREE_SHIPPING,
      applyTo: ShippingApplyTo.selected,
      selectedMethods: methods,
    }
  }

  return {
    type: BuyAndWinBenefitVariant.FREE_SHIPPING,
    applyTo: applyToAllShippings
      ? ShippingApplyTo.all
      : ShippingApplyTo.cheapest,
  }
}

export function mapMaximumUnitPriceDiscount(
  data: RnbCalculatorPayload
): MaximumUnitPriceDiscount {
  const { maximumUnitPriceDiscount } = data

  return {
    type: BuyAndWinBenefitVariant.MAXIMUM_UNIT_PRICE_DISCOUNT,
    price: maximumUnitPriceDiscount,
  }
}

export function mapBenefit(
  data: RnbCalculatorPayload
): BuyAndWinEffect['benefit'] {
  const {
    skusGift,
    percentualShippingDiscountValue,
    maximumUnitPriceDiscount,
  } = data

  if (mapGiftItems(skusGift).length) {
    return mapGift(data, BuyAndWinBenefitVariant.GIFT)
  }

  if (percentualShippingDiscountValue === 100) {
    return mapFreeShipping(data)
  }

  if (maximumUnitPriceDiscount) {
    return mapMaximumUnitPriceDiscount(data)
  }
}

export function mapBuyAndWinEffect(
  data: RnbCalculatorPayload
): BuyAndWinEffect {
  return {
    trigger: mapTrigger(data),
    benefit: mapBenefit(data),
  }
}

export function mapBuyAndWinRestrictions(
  data: RnbCalculatorPayload
): BuyAndWinRestrictions {
  return {
    maxUsage: mapUsage(data),
    maxUsagePerClient: mapMaxUsagePerClient(data),
    accumulate: mapAccumulate(data),
    budget: mapBudget(data),
    sellers: mapSellerIds(data),
    salesChannels: mapSalesChannels(data),
    origin: data.origin,
  }
}

export function mapBuyAndWin(data: RnbCalculatorPayload): BuyAndWinPromotion {
  return {
    ...mapPromotionBase(data),
    type: PROMOTION_TYPE.BUY_AND_WIN,
    conditions: mapConditions(data),
    restrictions: mapBuyAndWinRestrictions(data),
    buyAndWin: mapBuyAndWinEffect(data),
  }
}
